import { forwardRef } from 'react'
import type { InputHTMLAttributes } from 'react'

interface MaskedInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
  value: string
  onChange: (value: string) => void
  mask: 'cpf' | 'cnpj' | 'cpf-cnpj' | 'phone' | 'email' | 'cep' | 'currency'
  error?: string
}

export const MaskedInput = forwardRef<HTMLInputElement, MaskedInputProps>(
  ({ value, onChange, mask, error, className = '', ...props }, ref) => {
    const applyMask = (inputValue: string): string => {
      const cleaned = inputValue.replace(/\D/g, '')

      switch (mask) {
        case 'cpf': {
          const limited = cleaned.substring(0, 11)
          if (limited.length <= 3) return limited
          if (limited.length <= 6) return `${limited.slice(0, 3)}.${limited.slice(3)}`
          if (limited.length <= 9)
            return `${limited.slice(0, 3)}.${limited.slice(3, 6)}.${limited.slice(6)}`
          return `${limited.slice(0, 3)}.${limited.slice(3, 6)}.${limited.slice(6, 9)}-${limited.slice(9)}`
        }

        case 'cnpj': {
          const limited = cleaned.substring(0, 14)
          if (limited.length <= 2) return limited
          if (limited.length <= 5) return `${limited.slice(0, 2)}.${limited.slice(2)}`
          if (limited.length <= 8)
            return `${limited.slice(0, 2)}.${limited.slice(2, 5)}.${limited.slice(5)}`
          if (limited.length <= 12)
            return `${limited.slice(0, 2)}.${limited.slice(2, 5)}.${limited.slice(5, 8)}/${limited.slice(8)}`
          return `${limited.slice(0, 2)}.${limited.slice(2, 5)}.${limited.slice(5, 8)}/${limited.slice(8, 12)}-${limited.slice(12)}`
        }

        case 'cpf-cnpj': {
          const limited = cleaned.substring(0, 14)
          if (limited.length <= 11) {
            if (limited.length <= 3) return limited
            if (limited.length <= 6) return `${limited.slice(0, 3)}.${limited.slice(3)}`
            if (limited.length <= 9)
              return `${limited.slice(0, 3)}.${limited.slice(3, 6)}.${limited.slice(6)}`
            return `${limited.slice(0, 3)}.${limited.slice(3, 6)}.${limited.slice(6, 9)}-${limited.slice(9)}`
          }
          // CNPJ
          if (limited.length <= 2) return limited
          if (limited.length <= 5) return `${limited.slice(0, 2)}.${limited.slice(2)}`
          if (limited.length <= 8)
            return `${limited.slice(0, 2)}.${limited.slice(2, 5)}.${limited.slice(5)}`
          if (limited.length <= 12)
            return `${limited.slice(0, 2)}.${limited.slice(2, 5)}.${limited.slice(5, 8)}/${limited.slice(8)}`
          return `${limited.slice(0, 2)}.${limited.slice(2, 5)}.${limited.slice(5, 8)}/${limited.slice(8, 12)}-${limited.slice(12)}`
        }

        case 'phone': {
          const limited = cleaned.substring(0, 11)
          if (limited.length <= 2) return limited
          if (limited.length <= 6) return `(${limited.slice(0, 2)}) ${limited.slice(2)}`
          if (limited.length <= 10)
            return `(${limited.slice(0, 2)}) ${limited.slice(2, 6)}-${limited.slice(6)}`
          return `(${limited.slice(0, 2)}) ${limited.slice(2, 7)}-${limited.slice(7)}`
        }

        case 'cep': {
          const limited = cleaned.substring(0, 8)
          if (limited.length <= 5) return limited
          return `${limited.slice(0, 5)}-${limited.slice(5)}`
        }

        case 'email':
          return inputValue

        case 'currency': {
          const digits = cleaned.substring(0, 15)
          if (!digits) return ''

          const number = parseInt(digits, 10)
          const cents = (number % 100).toString().padStart(2, '0')
          const integer = Math.floor(number / 100).toString()
          const formattedInteger = integer.replace(/\B(?=(\d{3})+(?!\d))/g, '.')
          return `${formattedInteger},${cents}`
        }

        default:
          return inputValue
      }
    }

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const maskedValue = applyMask(e.target.value)
      onChange(maskedValue)
    }

    return (
      <div>
        <input
          ref={ref}
          type="text"
          value={value}
          onChange={handleChange}
          className={`
            w-full min-h-[44px] rounded-lg border-2 px-4 py-3 text-base transition-all
            ${
              error
                ? 'border-red-300 dark:border-red-700 focus:border-red-500 dark:focus:border-red-500'
                : 'border-gray-300 dark:border-gray-600 focus:border-primary-500 dark:focus:border-primary-500'
            }
            bg-white dark:bg-gray-800
            text-gray-900 dark:text-white
            placeholder-gray-400 dark:placeholder-gray-500
            focus:outline-none focus:ring-2
            ${error ? 'focus:ring-red-500/20' : 'focus:ring-primary-500/20'}
            disabled:cursor-not-allowed disabled:opacity-50
            ${className}
          `}
          {...props}
        />
        {error && <p className="mt-1.5 text-sm text-red-600 dark:text-red-400">{error}</p>}
      </div>
    )
  }
)

MaskedInput.displayName = 'MaskedInput'
